/** The user record's header (plan "Page anatomy" → Header) — the order / booking header
 *  pattern: a 32 px avatar before the name (the photo opens full size; without one, the
 *  navy initial), the kind chip beside it and a state chip only when the account is not
 *  plainly active (Deleted, Inactive — neutral, icon and word). The subtitle reads tenant ·
 *  email · phone · ID, each with its value actions.
 *
 *  The title never reads "null null": full name → email → `User #<id>`
 *  (`userDisplayName`). The Actions menu and Edit (`E`) join the right side with the
 *  editor (B-P4b). */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { Avatar, ButtonBase, Chip, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';

import type { ConsoleUserDetail } from '@/api/users';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { usersHref } from '@/utils/gridLinks';

import { UserKindChip } from '../UserKindChip';
import { emailActions, gridKindOf, phoneActions, type UserCtx } from '../userLinks';
import { userDisplayName } from '../userName';

const AVATAR_PX = 32;

const KIND_WORD: Record<ConsoleUserDetail['kind'], string> = { system: 'system', staff: 'staff', guest: 'guest' };

/** The person's initial for an avatar without a photo ("#" when there is no name and no
 *  email to take one from). */
export function userInitial(u: Pick<ConsoleUserDetail, 'first_name' | 'last_name' | 'email'>): string {
  const source = u.first_name?.trim() || u.last_name?.trim() || u.email?.trim() || '#';
  return source.charAt(0).toUpperCase();
}

/** The header avatar: the photo as a link to its original (a new tab), or the navy
 *  initial (`MuiAvatar` colorDefault) when there is none. */
export function UserAvatar({ user }: { user: ConsoleUserDetail }) {
  const size = { width: AVATAR_PX, height: AVATAR_PX, fontSize: 15, fontWeight: 600 };
  if (!user.avatar) {
    return (
      <Avatar aria-hidden sx={size} data-user-avatar="initial">
        {userInitial(user)}
      </Avatar>
    );
  }
  return (
    <Tooltip title="Open the photo full size">
      <ButtonBase
        component="a"
        href={user.avatar.original}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open the photo of ${userDisplayName(user)} full size`}
        data-user-avatar="photo"
        sx={{
          borderRadius: '50%',
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        <Avatar src={user.avatar.small} alt="" sx={size} />
      </ButtonBase>
    </Tooltip>
  );
}

export interface UserRecordHeaderProps {
  user: ConsoleUserDetail;
  /** The active page tab's label (document title). */
  section: string;
  actions?: ReactNode;
}

export function UserRecordHeader({ user, section, actions }: UserRecordHeaderProps) {
  const kindPath = gridKindOf(user.kind);
  const ctx: UserCtx = { tenantId: user.tour_operator?.id ?? null, kind: kindPath };
  const titleText = userDisplayName(user);
  const tenant = user.tour_operator;

  const chips = (
    <>
      <ValueActions chip actions={[act.filter(usersHref(kindPath), `All ${KIND_WORD[user.kind]} users`)]}>
        <UserKindChip kind={user.kind} />
      </ValueActions>
      {user.is_deleted ? (
        <ValueActions chip actions={[act.filter(usersHref(kindPath, { deleted: true }), 'Deleted users')]}>
          <Chip size="small" variant="outlined" icon={<DeleteOutlineIcon />} label="Deleted" data-user-state="deleted" />
        </ValueActions>
      ) : (
        !user.is_active && (
          <ValueActions chip actions={[act.filter(usersHref(kindPath, { is_active: false }), 'Inactive users')]}>
            <Chip size="small" variant="outlined" icon={<PersonOffOutlinedIcon />} label="Inactive" data-user-state="inactive" />
          </ValueActions>
        )
      )}
    </>
  );

  const subtitle = (
    <DetailSubtitle
      nowrap
      parts={[
        tenant && (
          <ValueActions
            actions={operatorActions(tenant, {
              href: usersHref(kindPath, { tenant: tenant.id }),
              label: `${KIND_WORD[user.kind]} users of this operator`,
            })}
          >
            {tenant.name || tenant.code}
          </ValueActions>
        ),
        user.email && <ValueActions actions={emailActions(user.email, ctx)}>{user.email}</ValueActions>,
        user.phone && <ValueActions actions={phoneActions(user.phone, ctx)}>{user.phone}</ValueActions>,
        <ValueActions key="id" actions={[act.copy(String(user.id), 'Copy id')]}>
          ID {user.id}
        </ValueActions>,
      ]}
    />
  );

  return (
    <PageHeader
      helpStory="users-detail-page"
      section={section}
      leading={<UserAvatar user={user} />}
      title={titleText}
      titleText={titleText}
      titleAdornment={chips}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
